import type {
  AccountSummary,
  CredentialRotationHistory,
  DeliveryHistoryEntry,
  AuthSessionState,
  CloudRalphConfig,
  LlmProvider,
  LlmProviderStatus,
  CloudRunMeta,
  DeliveryResult,
  GitHubConnectionStatus,
  GitHubRepo,
  StreamChunk
} from './shared/types'

const API_BASE = '/api'

class RalphApi {
  private abortController: AbortController | null = null
  private currentSessionId: string | null = null

  get sessionId() {
    return this.currentSessionId
  }

  async getSession(): Promise<AuthSessionState> {
    const res = await fetch(`${API_BASE}/auth/session`)
    if (!res.ok) {
      const fallback = await res.json().catch(() => ({ authenticated: false }))
      return fallback as AuthSessionState
    }
    return res.json()
  }

  async login(email: string, password: string): Promise<boolean> {
    const res = await fetch(`${API_BASE}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    })
    return res.ok
  }

  async register(email: string, password: string): Promise<boolean> {
    const res = await fetch(`${API_BASE}/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    })
    return res.ok
  }

  async checkAuth(): Promise<boolean> {
    const session = await this.getSession()
    return Boolean(session.authenticated)
  }

  async logout(): Promise<void> {
    await fetch(`${API_BASE}/auth/logout`, { method: 'POST' })
  }

  async getAccountSummary(): Promise<AccountSummary> {
    const res = await fetch(`${API_BASE}/account/me`)
    if (!res.ok) {
      throw new Error(await res.text())
    }
    return res.json()
  }

  async saveCredential(provider: 'anthropic' | 'minimax' | 'zai' | 'github', secret: string): Promise<void> {
    const res = await fetch(`${API_BASE}/account/credentials/${provider}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ secret })
    })
    if (!res.ok) {
      throw new Error(await res.text())
    }
  }

  async deleteCredential(provider: 'anthropic' | 'minimax' | 'zai' | 'github'): Promise<void> {
    const res = await fetch(`${API_BASE}/account/credentials/${provider}`, {
      method: 'DELETE'
    })
    if (!res.ok) {
      throw new Error(await res.text())
    }
  }

  async rotateCredential(
    provider: 'anthropic' | 'minimax' | 'zai' | 'github',
    secret: string,
    graceDays: number
  ): Promise<void> {
    const res = await fetch(`${API_BASE}/account/credentials/${provider}/rotate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ secret, graceDays })
    })
    if (!res.ok) {
      throw new Error(await res.text())
    }
  }

  async getCredentialRotations(): Promise<CredentialRotationHistory[]> {
    const res = await fetch(`${API_BASE}/account/credential-rotations`)
    if (!res.ok) {
      throw new Error(await res.text())
    }
    const data = await res.json() as { rotations: CredentialRotationHistory[] }
    return data.rotations
  }

  async getDeliveryHistory(): Promise<DeliveryHistoryEntry[]> {
    const res = await fetch(`${API_BASE}/account/delivery-history`)
    if (!res.ok) {
      throw new Error(await res.text())
    }
    const data = await res.json() as { deliveries: DeliveryHistoryEntry[] }
    return data.deliveries
  }

  async getExportOptions(): Promise<{ deliveryHistoryFields: string[] }> {
    const res = await fetch(`${API_BASE}/assets/export-options`)
    if (!res.ok) {
      throw new Error(await res.text())
    }
    return res.json()
  }

  async exportDeliveryHistoryCsv(fields: string[]): Promise<Blob> {
    const res = await fetch(`${API_BASE}/account/delivery-history/export`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ fields })
    })
    if (!res.ok) {
      throw new Error(await res.text())
    }
    return res.blob()
  }

  async getGitHubStatus(): Promise<GitHubConnectionStatus> {
    const res = await fetch(`${API_BASE}/github/status`)
    if (!res.ok) {
      throw new Error(await res.text())
    }
    return res.json()
  }

  async listGitHubRepos(): Promise<GitHubRepo[]> {
    const res = await fetch(`${API_BASE}/github/repos`)
    if (!res.ok) {
      throw new Error(await res.text())
    }
    const data = await res.json() as { repos: GitHubRepo[] }
    return data.repos
  }

  async getRun(sessionId: string): Promise<CloudRunMeta> {
    const res = await fetch(`${API_BASE}/runs/${sessionId}`)
    if (!res.ok) {
      throw new Error(await res.text())
    }
    return res.json()
  }

  async getLlmProviders(): Promise<LlmProviderStatus> {
    const res = await fetch(`${API_BASE}/llm/providers`)
    if (!res.ok) {
      throw new Error(await res.text())
    }
    return res.json()
  }

  async startLoop(
    config: CloudRalphConfig,
    callbacks: {
      onSession?: (sessionId: string) => void
      onChunk: (chunk: StreamChunk) => void
      onIterationStart: (data: { iteration: number; maxIterations: number }) => void
      onComplete: (state: { status: string; error?: string }) => void
      onStatusChange: (status: string) => void
    }
  ): Promise<void> {
    this.abortController = new AbortController()

    const res = await fetch(`${API_BASE}/loop/start`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(config),
      signal: this.abortController.signal
    })

    if (!res.ok) {
      const err = await res.text()
      callbacks.onComplete({ status: 'error', error: err })
      return
    }

    if (!res.body) {
      callbacks.onComplete({ status: 'error', error: 'No response body' })
      return
    }

    callbacks.onStatusChange('running')

    const reader = res.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''

    try {
      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() || ''

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue
          const data = line.slice(6)
          if (data === '[DONE]') {
            callbacks.onComplete({ status: 'complete' })
            return
          }

          try {
            const event = JSON.parse(data)

            switch (event.type) {
              case 'session':
                this.currentSessionId = event.sessionId
                callbacks.onSession?.(event.sessionId)
                break
              case 'iteration':
                callbacks.onIterationStart({
                  iteration: event.iteration,
                  maxIterations: event.maxIterations
                })
                break
              case 'chunk':
                callbacks.onChunk(event.chunk)
                break
              case 'status':
                callbacks.onStatusChange(event.status)
                break
              case 'complete':
                callbacks.onComplete({
                  status: event.status,
                  error: event.error
                })
                return
              case 'error':
                callbacks.onComplete({
                  status: 'error',
                  error: event.error
                })
                return
            }
          } catch {
            // Skip malformed JSON
          }
        }
      }
    } catch (err: unknown) {
      if (err instanceof Error && err.name === 'AbortError') {
        callbacks.onComplete({ status: 'cancelled' })
      } else {
        callbacks.onComplete({
          status: 'error',
          error: err instanceof Error ? err.message : 'Unknown error'
        })
      }
    }
  }

  async cancelLoop(): Promise<void> {
    this.abortController?.abort()
    this.abortController = null

    try {
      await fetch(`${API_BASE}/loop/cancel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId: this.currentSessionId })
      })
    } catch {
      // Best effort
    }
  }

  async generatePrd(
    userPrompt: string,
    systemPrompt: string,
    llmProvider: LlmProvider,
    callbacks: {
      onChunk: (text: string) => void
      onComplete: () => void
    }
  ): Promise<void> {
    this.abortController = new AbortController()

    const res = await fetch(`${API_BASE}/prd/generate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userPrompt, systemPrompt, llmProvider }),
      signal: this.abortController.signal
    })

    if (!res.ok || !res.body) {
      callbacks.onComplete()
      return
    }

    const reader = res.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''

    try {
      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() || ''

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue
          const data = line.slice(6)
          if (data === '[DONE]') {
            callbacks.onComplete()
            return
          }
          try {
            const event = JSON.parse(data)
            if (event.text) callbacks.onChunk(event.text)
          } catch {
            // Skip malformed
          }
        }
      }
    } catch {
      // Aborted or error
    }
    callbacks.onComplete()
  }

  getDownloadUrl(): string {
    if (!this.currentSessionId) return ''
    return `${API_BASE}/assets/download?session=${this.currentSessionId}`
  }

  async deliverToGithub(repoFullName: string, baseBranch: string): Promise<DeliveryResult> {
    if (!this.currentSessionId) {
      return { success: false, error: 'No session to publish' }
    }

    const res = await fetch(`${API_BASE}/github/deploy`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        sessionId: this.currentSessionId,
        repoFullName,
        baseBranch
      })
    })

    return res.json()
  }
}

export const ralphApi = new RalphApi()
